import {
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import licencasService from "../../services/licencas";
import licencaEmitidaService from "../../services/licencaEmitida";
import autorizacoesService from "../../services/autorizacoes";
import AutorizacaoStatusChip from "../../components/AutorizacaoStatusChip";
import StatusChip from "../../components/StatusChip";
import protocoloService from "../../services/protocolo";
import usuarioService from "../../services/usuario";
import {
  statusLabelsEnum,
  mascaraStatusEmpreendedor,
} from "../../utils/constants";
import {
  getAtividadeFlorestalResumo,
  PROTOCOLO_STATUS_EMITIDO,
  protocoloStatusLabels,
} from "../../utils/protocoloConstants";
import {
  getMuiChipColor,
  getProtocoloPaletteKey,
} from "../../utils/statusPalette";
import { baixarRequerimentoProtocoloPdf } from "../../utils/requerimentoProtocoloPdf";
import { formatDateToShow } from "../../utils/formatters";

const statCardPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  textAlign: "center",
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  color: "text.primary",
};

const statCardValueSx = {
  fontSize: { xs: "2rem", md: "3rem" },
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    aguardandoAnalise: 0,
    analistaAnalisando: 0,
    aguardandoInfo: 0,
    aguardandoPagamento: 0,
    aprovadas: 0,
    rejeitadas: 0,
  });
  const [recentLicenses, setRecentLicenses] = useState([]);
  const [recentAutorizacoes, setRecentAutorizacoes] = useState([]);
  const [recentProtocolos, setRecentProtocolos] = useState([]);
  const [downloadingProtocoloId, setDownloadingProtocoloId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [condicionantesPorLicenca, setCondicionantesPorLicenca] = useState({});

  const handleDownloadLicenca = async (solicitacaoId) => {
    try {
      const response =
        await licencaEmitidaService.obterPorSolicitacaoDeLicencaId(
          solicitacaoId,
        );
      const licencaData = response.data;

      let blob;
      if (typeof licencaData.fileContent === "string") {
        const byteCharacters = atob(licencaData.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], {
          type: licencaData.contextType || "application/pdf",
        });
      } else {
        const byteArray = new Uint8Array(licencaData.fileContent);
        blob = new Blob([byteArray], {
          type: licencaData.contextType || "application/pdf",
        });
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Erro ao fazer download da licença:", error);
      alert("Erro ao fazer download da licença. Tente novamente.");
    }
  };

  const handleDownloadRequerimento = async (protocolo) => {
    if (protocolo.status !== PROTOCOLO_STATUS_EMITIDO) return;

    setDownloadingProtocoloId(protocolo.id);
    try {
      const detalhes = await protocoloService.obterPorId(protocolo.id);
      const usuario = await usuarioService.obterDados();
      await baixarRequerimentoProtocoloPdf(detalhes, usuario);
    } catch (error) {
      console.error("Erro ao baixar requerimento:", error);
      alert("Erro ao baixar o requerimento. Tente novamente.");
    } finally {
      setDownloadingProtocoloId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const data = await licencasService.listarMinhasSolicitacoes();

        const calculatedStats = {
          total: data.length,
          aguardandoAnalise: data.filter(
            (l) => l.status === statusLabelsEnum.AGUARDANDO_ANALISE,
          ).length,
          analistaAnalisando: data.filter(
            (l) => l.status === statusLabelsEnum.ANALISTA_ANALISANDO,
          ).length,
          aguardandoInfo: data.filter(
            (l) => l.status === statusLabelsEnum.SOLICITANDO_INFORMACOES,
          ).length,
          aguardandoPagamento: data.filter(
            (l) => l.status === statusLabelsEnum.AGUARDANDO_PAGAMENTO,
          ).length,
          aprovadas: data.filter((l) => l.status === statusLabelsEnum.APROVADA)
            .length,
          rejeitadas: data.filter(
            (l) => l.status === statusLabelsEnum.REJEITADA,
          ).length,
        };

        setStats(calculatedStats);

        const sortedLicenses = [...data]
          .sort((a, b) => new Date(b.createAt) - new Date(a.createAt))
          .slice(0, 3);

        setRecentLicenses(sortedLicenses);

        try {
          const autResp = await autorizacoesService.listarMinhasSolicitacoes({
            pageNumber: 1,
            pageSize: 10,
          });
          const autItems = autResp?.items || [];
          const sortedAut = [...autItems]
            .sort((a, b) => new Date(b.createAt) - new Date(a.createAt))
            .slice(0, 3);
          setRecentAutorizacoes(sortedAut);
        } catch (err) {
          console.error("Erro ao buscar autorizações recentes:", err);
        }

        try {
          const protoResp = await protocoloService.listarMinhas(1, 10);
          const protoItems = protoResp?.data ?? [];
          const sortedProto = [...protoItems]
            .sort((a, b) => new Date(b.createAt) - new Date(a.createAt))
            .slice(0, 3);
          setRecentProtocolos(sortedProto);
        } catch (err) {
          console.error("Erro ao buscar protocolos recentes:", err);
        }
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const CondicionantesBadge = ({ licencaId }) => {
    const condicionantes = condicionantesPorLicenca[licencaId] || [];

    if (condicionantes.length === 0) {
      return null;
    }

    return (
      <Tooltip
        title={`${condicionantes.length} condicionante(s) aguardando resposta`}
      >
        <Badge
          badgeContent={condicionantes.length}
          color="error"
          sx={{ ml: 1 }}
        >
          <AssignmentIcon color="warning" />
        </Badge>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Container sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Carregando seu dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box mb={{ xs: 3, md: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
        >
          Bem-vindo, {user?.username || "Usuário"}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Aqui está um resumo das suas solicitações de licenciamento ambiental
        </Typography>
      </Box>

      <Box mb={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              fullWidth
              size="large"
              onClick={() => navigate("/nova-solicitacao")}
              sx={{ py: 1.5 }}
            >
              Nova Solicitação de Licença
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DescriptionIcon />}
              fullWidth
              size="large"
              onClick={() => navigate("/minhas-solicitacoes")}
              sx={{ py: 1.5 }}
            >
              Ver Todas as Solicitações
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box mb={4}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}
        >
          Resumo das Solicitações
        </Typography>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper elevation={0} sx={statCardPaperSx}>
              <Typography variant="h3" color="primary" sx={statCardValueSx}>
                {stats.total}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Total de Solicitações
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper elevation={0} sx={statCardPaperSx}>
              <Typography
                variant="h3"
                sx={{ ...statCardValueSx, color: "success.main" }}
              >
                {stats.aprovadas}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Aprovadas
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper elevation={0} sx={statCardPaperSx}>
              <Typography
                variant="h3"
                sx={{ ...statCardValueSx, color: "warning.main" }}
              >
                {stats.aguardandoAnalise +
                  stats.analistaAnalisando +
                  stats.aguardandoInfo +
                  stats.aguardandoPagamento}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Em Andamento
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box mb={4}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: 1,
                borderColor: "divider",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Detalhamento por Status
          </Typography>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Aguardando Análise
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total
                      ? (stats.aguardandoAnalise / stats.total) * 100
                      : 0
                  }
                  color="primary"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.aguardandoAnalise}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Analista Analisando
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total
                      ? (stats.analistaAnalisando / stats.total) * 100
                      : 0
                  }
                  color="primary"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.analistaAnalisando}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Aguardando Informações
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total ? (stats.aguardandoInfo / stats.total) * 100 : 0
                  }
                  color="info"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.aguardandoInfo}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Aguardando Pagamento
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total
                      ? (stats.aguardandoPagamento / stats.total) * 100
                      : 0
                  }
                  color="info"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.aguardandoPagamento}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Aprovadas
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total ? (stats.aprovadas / stats.total) * 100 : 0
                  }
                  color="success"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.aprovadas}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Rejeitadas
            </Typography>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.total ? (stats.rejeitadas / stats.total) * 100 : 0
                  }
                  color="error"
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="text.secondary">
                  {stats.rejeitadas}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
          Solicitações Recentes
        </Typography>

        {recentLicenses.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <List sx={{ width: "100%" }}>
              {recentLicenses.map((license, index) => (
                <React.Fragment key={license.id}>
                  <ListItem
                    button
                    onClick={() => navigate(`/solicitacao/${license.id}`)}
                    sx={{
                      py: 2,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <DescriptionIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="subtitle1" component="span">
                            {license.nomeTipoLicenca}
                          </Typography>
                          <CondicionantesBadge licencaId={license.id} />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                          >
                            {license.empreendimento.nome}
                          </Typography>

                          {condicionantesPorLicenca[license.id] &&
                            condicionantesPorLicenca[license.id].length > 0 && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1,
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.warning.main, 0.16),
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "warning.main",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "warning.main" }}
                                >
                                  <strong>Atenção:</strong>{" "}
                                  {condicionantesPorLicenca[license.id].length}{" "}
                                  documento(s) condicionante(s) pendente(s) de
                                  resposta.
                                </Typography>
                              </Box>
                            )}
                        </>
                      }
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box mr={1}>
                        <StatusChip
                          status={mascaraStatusEmpreendedor(license.status)}
                        />
                      </Box>
                      {license.status === statusLabelsEnum.LICENCA_ASSINADA && (
                        <Tooltip title="Download da Licença">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadLicenca(license.id);
                            }}
                          >
                            Download
                          </Button>
                        </Tooltip>
                      )}
                      <ArrowForwardIcon color="action" fontSize="small" />
                    </Box>
                  </ListItem>
                  {index < recentLicenses.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Você ainda não possui solicitações de licenciamento.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/nova-solicitacao")}
              sx={{ mt: 2 }}
            >
              Criar Nova Solicitação
            </Button>
          </Paper>
        )}
      </Box>

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
          Autorizações Recentes
        </Typography>

        {recentAutorizacoes.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <List sx={{ width: "100%" }}>
              {recentAutorizacoes.map((aut, index) => {
                return (
                  <React.Fragment key={aut.id}>
                    <ListItem
                      button
                      onClick={() => navigate(`/autorizacao/${aut.id}`)}
                      sx={{
                        py: 2,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span">
                            {aut.nomeEvento || "Autorização"}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                          >
                            {aut.localEvento || "Local não informado"}
                          </Typography>
                        }
                      />
                      <Box display="flex" alignItems="center" gap={1}>
                        <AutorizacaoStatusChip status={aut.status} />
                        <ArrowForwardIcon color="action" fontSize="small" />
                      </Box>
                    </ListItem>
                    {index < recentAutorizacoes.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Você ainda não possui autorizações.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/nova-autorizacao")}
              sx={{ mt: 2 }}
            >
              Solicitar Autorização
            </Button>
          </Paper>
        )}
      </Box>

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
          Protocolos Recentes
        </Typography>

        {recentProtocolos.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <List sx={{ width: "100%" }}>
              {recentProtocolos.map((protocolo, index) => {
                return (
                  <React.Fragment key={protocolo.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemIcon>
                        <AssignmentIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span">
                            {protocolo.numeroProcesso || "—"} —{" "}
                            {protocolo.processoNome || "Protocolo"}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                          >
                            {getAtividadeFlorestalResumo(
                              protocolo.atividadeEmpreendimento,
                            )}
                            {protocolo.createAt
                              ? ` • ${formatDateToShow(protocolo.createAt)}`
                              : ""}
                          </Typography>
                        }
                      />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          size="small"
                          label={protocoloStatusLabels[protocolo.status] ?? protocolo.status}
                          color={getMuiChipColor(getProtocoloPaletteKey(protocolo.status))}
                        />
                        {protocolo.status === PROTOCOLO_STATUS_EMITIDO && (
                          <Tooltip title="Baixar REQUERIMENTO">
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label="Baixar REQUERIMENTO"
                              disabled={downloadingProtocoloId === protocolo.id}
                              onClick={() =>
                                handleDownloadRequerimento(protocolo)
                              }
                            >
                              {downloadingProtocoloId === protocolo.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DownloadIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                    {index < recentProtocolos.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: 1,
                borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Você ainda não possui protocolos florestais.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Protocolos são criados automaticamente ao solicitar licença para
              empreendimentos de atividades florestais.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/nova-solicitacao")}
            >
              Nova Solicitação
            </Button>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default UserDashboard;
